import PolicyBody from "./PolicyBody";
import useScrolledToEnd from "./useScrolledToEnd";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ConsentStatusQuery, useAcceptPoliciesMutation } from "generated.hooks";
import useAuth from "hooks/useAuth";
import React from "react";

type PendingPolicy =
  ConsentStatusQuery["consentStatus"]["pendingPolicies"][number];

interface ReConsentDialogProps {
  pendingPolicies: readonly PendingPolicy[];
  /** Re-runs the guard's consent query once acceptance is recorded. */
  onAccepted: () => Promise<unknown> | void;
}

const LABELS: Record<string, { name: string; href: string }> = {
  privacy: { name: "Privacy Policy", href: "/legal/privacy" },
  terms: { name: "Terms of Service", href: "/legal/terms" },
};

/**
 * Blocks the app behind a modal when a published policy has been superseded
 * since the user last accepted.
 *
 * A modal over the normal layout rather than a replacement screen. Replacing
 * the layout also removed the themed page background and the app chrome that
 * come with it, so the gate rendered as a dark card on an unthemed body; the
 * dialog inherits the theme like any other component and the dimmed app sits
 * behind it, which also tells the user where they are.
 *
 * Deliberately not dismissible: no escape key, no backdrop close, no close
 * button. The two ways out are accepting and signing out, both explicit.
 * Blocking is interactive-only - telemetry ingestion is never consent-gated,
 * so a device streaming mid-session keeps working regardless.
 *
 * The documents are rendered inline rather than linked. Asking someone to
 * accept text they have to leave the page to read is both worse UX and weaker
 * evidence that the consent was informed - the link is kept as a secondary
 * affordance for reading the full page in a new tab.
 */
function ReConsentDialog({
  pendingPolicies,
  onAccepted,
}: ReConsentDialogProps) {
  const [acceptPolicies, { loading }] = useAcceptPoliciesMutation();
  const { signOut } = useAuth();
  const [error, setError] = React.useState<string | null>(null);
  const theme = useTheme();
  // A policy is long. On a phone a constrained dialog would be mostly chrome.
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Accepting is held back until the documents have been scrolled through, so
  // the acceptance is against text that was actually put in front of the user.
  const { rootRef, sentinelRef, reachedEnd } = useScrolledToEnd();

  const onAccept = async () => {
    setError(null);
    try {
      const res = await acceptPolicies({
        variables: {
          acceptances: pendingPolicies.map((policy) => ({
            documentType: policy.documentType,
            version: policy.version,
          })),
        },
      });
      if (res.data?.acceptPolicies?.ok) {
        await onAccepted();
      } else {
        setError(
          res.data?.acceptPolicies?.errors?.[0]?.message ??
            "Your acceptance could not be recorded. Please try again."
        );
      }
    } catch (acceptError) {
      console.error("Failed to record policy acceptance", acceptError);
      setError("Your acceptance could not be recorded. Please try again.");
    }
  };

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      // `scroll="paper"` keeps the actions pinned while the documents scroll,
      // so the accept button cannot end up below the fold however long the
      // policy is. That is why the bodies are not individually scrollable
      // here - one scroll region reads far better than three nested ones.
      scroll="paper"
      disableEscapeKeyDown
      aria-labelledby="reconsent-title"
      // MUI hides sibling content with `aria-hidden` and traps focus, but does
      // not set `aria-modal` itself. Stating it is what tells a screen reader
      // the rest of the app is out of play, matching what the scrim conveys
      // visually. It goes through PaperProps because Dialog spreads its own
      // aria-* onto the Modal root, not onto the element carrying role=dialog.
      PaperProps={{ "aria-modal": "true" }}
      data-testid="reconsent-dialog"
    >
      <DialogTitle id="reconsent-title">
        We have updated our policies
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
          Please review and accept the following before continuing.
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        ref={rootRef}
        // Focusable so the documents can be scrolled with the keyboard alone -
        // otherwise the gate would be unsatisfiable without a pointer.
        tabIndex={0}
      >
        {pendingPolicies.map((policy) => {
          const label = LABELS[policy.documentType] ?? {
            name: policy.documentType,
            href: "#",
          };
          return (
            <Box
              key={`${policy.documentType}-${policy.version}`}
              sx={{ "&:not(:first-of-type)": { mt: 4 } }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="h6">{label.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  Version {policy.version}
                  {policy.effectiveDate
                    ? ` — effective ${policy.effectiveDate}`
                    : ""}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Link
                  href={label.href}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                >
                  Open in a new tab
                </Link>
              </Box>
              <PolicyBody
                data-testid={`policy-body-${policy.documentType}`}
                body={policy.body}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  // Theme-aware tint rather than a fixed colour, so the panel
                  // reads as inset in both light and dark.
                  bgcolor: "action.hover",
                }}
              />
            </Box>
          );
        })}

        {error && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {/*
          Marks the end of the documents. Its visibility is what unlocks the
          accept button, which means content too short to scroll unlocks it
          immediately rather than being impossible to accept.
        */}
        <Box ref={sentinelRef} data-testid="reconsent-end" sx={{ height: 1 }} />
      </DialogContent>

      <DialogActions
        sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, p: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {/*
            Declining must always be possible, and must not be a trap: the
            account and its data are untouched. Self-service export and
            deletion are not built yet, so this points at support rather than
            promising something that does not exist.
          */}
          <Button
            variant="text"
            onClick={() => signOut()}
            data-testid="reconsent-decline"
          >
            Sign out
          </Button>
          <Button
            variant="contained"
            onClick={onAccept}
            disabled={loading || pendingPolicies.length === 0 || !reachedEnd}
            data-testid="reconsent-accept"
          >
            Accept and continue
          </Button>
        </Box>
        {!reachedEnd && (
          // Says why the button is disabled. A control that is simply inert,
          // with no explanation, reads as a broken page.
          <Typography
            variant="caption"
            color="textSecondary"
            data-testid="reconsent-scroll-hint"
          >
            Scroll to the end of the documents to continue.
          </Typography>
        )}
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="textSecondary">
          If you do not want to accept, you can sign out. Your account and data
          are not deleted; contact support if you would like them removed.
        </Typography>
      </DialogActions>
    </Dialog>
  );
}

export default ReConsentDialog;
